import React from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';

import {
  CountryEnum,
  CountryDisplayMap,
  CaStateOrProvinceDisplayMap,
  UsStateOrProvinceDisplayMap,
  UsStateOrProvinceEnum,
  CaStateOrProvinceEnum,
  Address,
  isUsStateOrProvinceEnum,
  isCaStateOrProvinceEnum,
  AddressInput
} from '_lib/api/apiTypes.shim';

import { Grid } from '_lib/components/Grid/Grid';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';

import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import type { FieldPathsMatching } from '_lib/types/hookForm.types';

export type AddressFormProps<
  T extends AddressInput,
  TNested extends FieldValues,
  TNestedPath extends FieldPathsMatching<TNested, T>
> =
  | {
      isNested: false;
      formContext: UseFormReturn<T>;
      disabled?: boolean;
      optional?: boolean;
      isEditingExisting: boolean;
    }
  | {
      isNested: true;
      formContext: UseFormReturn<TNested>;
      nestedPath: TNestedPath;
      disabled?: boolean;
      optional?: boolean;
      isEditingExisting: boolean;
    };

/**
 * @returns a React Hook Form FieldPath for a generic that extends an Address OR a generic that HAS an address nested.
 * The return value type is intentionally a FieldPath<Address> so that it can be used with the concrete type: UseFormReturn<Address>.
 * If RHF improves their API to better support reusable components, then we could allow this type to 100% true and support
 * the nested FieldPath.
 */
const getPath = <
  T extends AddressInput,
  TNested extends FieldValues,
  TNestedPath extends FieldPathsMatching<TNested, T>,
  TFieldPath extends FieldPath<Address>
>(
  props: AddressFormProps<T, TNested, TNestedPath>,
  field: TFieldPath
): TFieldPath => (props.isNested ? `${props.nestedPath}.${field}` : field) as TFieldPath;

// shouldn't have to use `as` at all, however there is a slight imperfection with our keys in CountryDisplayMap
const countryList = Object.entries(CountryDisplayMap).map(([enumValue, label]) => ({
  value: enumValue as CountryEnum,
  label
}));

const DEFAULT_COUNTRY = CountryEnum.US;

const getStateOrProvinceList = (countryCode: CountryEnum | undefined) => {
  if (!countryCode) {
    return Object.entries(CaStateOrProvinceDisplayMap)
      .concat(Object.entries(UsStateOrProvinceDisplayMap))
      .map(([enumValue, label]) => {
        return { value: enumValue as CaStateOrProvinceEnum | UsStateOrProvinceEnum, label };
      });
  } else if (countryCode === CountryEnum.US) {
    return Object.entries(UsStateOrProvinceDisplayMap).map(([enumValue, label]) => ({
      value: enumValue as UsStateOrProvinceEnum,
      label
    }));
  } else {
    return Object.entries(CaStateOrProvinceDisplayMap).map(([enumValue, label]) => {
      return { value: enumValue as CaStateOrProvinceEnum, label };
    });
  }
};

export const BaseAddressForm = <
  T extends AddressInput,
  TNested extends FieldValues,
  TNestedPath extends FieldPathsMatching<TNested, T>
>(
  props: AddressFormProps<T, TNested, TNestedPath>
) => {
  const { disabled, isEditingExisting, formContext, optional } = props;
  const { control, watch, trigger, setValue } = formContext as unknown as UseFormReturn<Address>;

  const isEditingAndNoCountry = isEditingExisting && watch(getPath(props, 'country')) === undefined;

  return (
    <>
      <Grid item xs={12} sm={6}>
        <TextFieldFormItem
          disabled={disabled}
          name={getPath(props, 'addressLine1')}
          label='Address Line 1'
          control={control}
          required={!optional}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextFieldFormItem
          disabled={disabled}
          name={getPath(props, 'addressLine2')}
          label='Address Line 2'
          control={control}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextFieldFormItem
          disabled={disabled}
          name={getPath(props, 'city')}
          label='City'
          control={control}
          required={!optional}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Tooltip title={isEditingAndNoCountry ? 'Select a Country' : ''}>
          <span>
            <SelectFormItem
              name={getPath(props, 'stateOrProvince')}
              control={control}
              label='State or Province'
              disabled={disabled || isEditingAndNoCountry}
              required={!optional}>
              {getStateOrProvinceList(watch(getPath(props, 'country'))).map((stateOrProvince) => (
                <MenuItem key={stateOrProvince.value} value={stateOrProvince.value}>
                  {stateOrProvince.label}
                </MenuItem>
              ))}
            </SelectFormItem>
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextFieldFormItem
          disabled={disabled}
          name={getPath(props, 'zipOrPostal')}
          label='Zip Code'
          control={control}
          required={!optional}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectFormItem
          name={getPath(props, 'country')}
          control={control}
          defaultValue={isEditingExisting ? undefined : DEFAULT_COUNTRY}
          label='Country'
          disabled={disabled}
          required={!optional}
          onChange={(e) => {
            const currentCountry = e.target.value;
            const currentStateOrProvince = watch(getPath(props, 'stateOrProvince'));
            if (
              (isUsStateOrProvinceEnum(currentStateOrProvince) && currentCountry !== CountryEnum.US) ||
              (isCaStateOrProvinceEnum(currentStateOrProvince) && currentCountry !== CountryEnum.CA)
            ) {
              setValue(getPath(props, 'stateOrProvince'), '' as UsStateOrProvinceEnum | CaStateOrProvinceEnum);
              if (isEditingExisting) trigger(getPath(props, 'stateOrProvince'), { shouldFocus: true });
            }
          }}>
          {countryList.map((country) => (
            <MenuItem key={country.value} value={country.value}>
              {country.label}
            </MenuItem>
          ))}
        </SelectFormItem>
      </Grid>
    </>
  );
};
